"use client";

import { Search } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { useDeferredValue, useMemo, useState } from "react";
import {
  ConnectButton,
  type ConnectButtonAppearance,
  type ConnectSurface,
} from "@/components/buddy";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useBuddyRelationships } from "@/hooks/use-buddy-relationships";
import type { BuddyRelationship } from "@/lib/buddy/relationship";
import { cn } from "@/lib/utils";

export interface SearchableUserListBadge {
  label: string;
  variant?: ComponentProps<typeof Badge>["variant"];
  className?: string;
  /**
   * `trailing` (default) sits in the right-hand action cluster, which never
   * shrinks. `subtitle` is a compact tag at the start of the second line, so
   * a row that also carries a connect button and a "…" still fits a phone —
   * a trailing badge there pushed the button off the row.
   */
  placement?: "trailing" | "subtitle";
}

export interface SearchableUserListItem {
  id: string;
  /** Stable row key when one user can appear more than once. */
  key?: string;
  name: string;
  username?: string | null;
  image?: string | null;
  href?: string | null;
  isBlocked?: boolean;
  secondaryText?: string | null;
  badges?: SearchableUserListBadge[];
  searchText?: string | null;
  /**
   * Set false for rows that can never be added — the viewer's own row, and
   * guest attendees who have no account behind them. Ignored entirely by lists
   * that do not pass a `connectSurface`.
   */
  canConnect?: boolean;
  trailingAction?: ReactNode;
}

interface SearchableUserListProps {
  onNavigate?: () => void;
  items: SearchableUserListItem[];
  emptyMessage?: string;
  searchPlaceholder?: string;
  className?: string;
  bleed?: boolean;
  /**
   * Opt in to a quick-add button on every row. Omit for lists where adding
   * makes no sense (a picker, a roster the viewer is managing).
   */
  connectSurface?: ConnectSurface;
  /**
   * Which treatment the row buttons use. `glass` (the default) is the pill
   * built to sit over photos; `solid` is the filled brand button that matches
   * discover's list rows. Same behaviour either way — only the look differs.
   */
  connectAppearance?: ConnectButtonAppearance;
  /** A preview of a longer roster: rows only, no search box. */
  hideSearch?: boolean;
  /** Focus the search box on mount — the viewer came here to search. */
  autoFocusSearch?: boolean;
  /** Extra classes for every row (padding/divider tone in edge-to-edge lists). */
  rowClassName?: string;
}

export function SearchableUserList({
  items,
  onNavigate,
  emptyMessage = "No people found.",
  searchPlaceholder = "Search",
  className,
  bleed = false,
  connectSurface,
  connectAppearance = "glass",
  hideSearch = false,
  autoFocusSearch = false,
  rowClassName,
}: SearchableUserListProps) {
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const normalizedQuery = deferredQuery.trim().toLowerCase();

  // Resolved for the whole roster, not the filtered slice, so typing in the
  // search box never refires the lookup.
  const connectableIds = useMemo(
    () =>
      connectSurface
        ? items
            .filter((item) => item.canConnect !== false && !item.isBlocked)
            .map((item) => item.id)
        : [],
    [connectSurface, items],
  );
  const { relationshipOf } = useBuddyRelationships(connectableIds);

  const filteredItems =
    normalizedQuery.length === 0
      ? items
      : items.filter((item) => {
          const haystack = [
            item.name,
            item.username,
            item.secondaryText,
            item.searchText,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return haystack.includes(normalizedQuery);
        });

  if (items.length === 0) {
    return (
      <div
        className={cn(
          "rounded-2xl border border-dashed border-border/70 bg-surface/40 p-8 text-center text-text-secondary",
          className,
        )}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      {hideSearch ? null : (
        <div className="relative">
          {/* z-10: the input's backdrop-blur paints it over an unlayered icon. */}
          <Search className="pointer-events-none absolute top-1/2 left-3 z-10 h-4 w-4 -translate-y-1/2 text-text-secondary dark:text-white/70" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            autoFocus={autoFocusSearch}
            className="h-11 rounded-2xl border-border/70 bg-surface pl-9 shadow-sm"
          />
        </div>
      )}

      {normalizedQuery.length > 0 ? (
        <p className="px-1 text-xs text-text-secondary">
          Showing {filteredItems.length} of {items.length}
        </p>
      ) : null}

      {filteredItems.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border/70 bg-surface/40 p-8 text-center text-text-secondary">
          No matches for your search.
        </div>
      ) : (
        <div className={cn(bleed ? "-mx-3" : undefined)}>
          {filteredItems.map((item) => (
            <SearchableUserListRow
              key={item.key ?? item.id}
              item={item}
              onNavigate={onNavigate}
              connectSurface={connectSurface}
              connectAppearance={connectAppearance}
              className={rowClassName}
              relationship={
                connectSurface && item.canConnect !== false && !item.isBlocked
                  ? relationshipOf(item.id)
                  : undefined
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface SearchableUserListSkeletonProps {
  rowCount?: number;
  className?: string;
  bleed?: boolean;
  /**
   * Match the appearance the real list will render. The two buttons are not the
   * same width — `glass` sizes to its label, `solid` is a fixed 6.5rem — so a
   * placeholder drawn at one width makes the rows jump when the data lands.
   */
  connectAppearance?: ConnectButtonAppearance;
  hideSearch?: boolean;
}

export function SearchableUserListSkeleton({
  rowCount = 4,
  className,
  bleed = false,
  connectAppearance = "glass",
  hideSearch = false,
}: SearchableUserListSkeletonProps) {
  const skeletonKeys = Array.from(
    { length: rowCount },
    (_, index) => `searchable-user-list-skeleton-${index}`,
  );
  // h-8 w-20 is the `glass` button at `size="sm"`; `solid` is one fixed
  // 6.5rem rounded-lg box for every label.
  const buttonPlaceholderClassName =
    connectAppearance === "solid"
      ? "h-8 w-[6.5rem] rounded-lg"
      : "h-8 w-20 rounded-full";

  return (
    <div className={cn("space-y-4", className)}>
      {hideSearch ? null : (
        <div className="relative animate-pulse">
          {/* z-10: the input's backdrop-blur paints it over an unlayered icon. */}
          <Search className="pointer-events-none absolute top-1/2 left-3 z-10 h-4 w-4 -translate-y-1/2 text-text-secondary dark:text-white/70" />
          <div className="h-11 rounded-2xl bg-surface-alt pl-9" />
        </div>
      )}

      <div className={cn(bleed ? "-mx-3" : undefined)}>
        {skeletonKeys.map((key) => (
          <div
            key={key}
            className="flex animate-pulse items-center gap-3 border-b border-border/50 px-3 py-3 last:border-b-0"
          >
            <div className="h-11 w-11 rounded-[14px] bg-surface-alt" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-4 w-32 rounded bg-surface-alt" />
              <div className="h-3 w-24 rounded bg-surface-alt" />
            </div>
            {/* Sized to the real ConnectButton for the appearance in use. */}
            <div className={cn("bg-surface-alt", buttonPlaceholderClassName)} />
          </div>
        ))}
      </div>
    </div>
  );
}

function SearchableUserListRow({
  item,
  onNavigate,
  connectSurface,
  connectAppearance,
  relationship,
  className,
}: {
  item: SearchableUserListItem;
  className?: string;
  onNavigate?: () => void;
  connectSurface?: ConnectSurface;
  connectAppearance?: ConnectButtonAppearance;
  relationship?: BuddyRelationship;
}) {
  const isBlocked = Boolean(item.isBlocked);
  const secondaryText = isBlocked
    ? "Blocked profile"
    : (item.secondaryText ?? (item.username ? `@${item.username}` : null));
  // Kept as the surface (not a boolean) so it narrows to a defined
  // `ConnectSurface` where the button is rendered.
  const connectAs =
    connectSurface && !isBlocked && item.canConnect !== false
      ? connectSurface
      : null;
  const subtitleBadges =
    item.badges?.filter((badge) => badge.placement === "subtitle") ?? [];
  const trailingBadges =
    item.badges?.filter((badge) => badge.placement !== "subtitle") ?? [];

  const rowContent = (
    <div className="flex items-center gap-3">
      {/* Squircle, not a circle: person avatars are 0.32 × their size
          everywhere else (feed header, chat, story tray), so a round one here
          read as a different component. */}
      <Avatar className="h-11 w-11 shrink-0 rounded-[14px] border border-border/70">
        <AvatarImage
          src={!isBlocked ? (item.image ?? undefined) : undefined}
          alt={item.name}
        />
        <AvatarFallback className="rounded-none bg-gradient-to-br from-accent/20 to-brand/20 text-sm font-semibold text-text-primary">
          {initials(isBlocked ? "Blocked user" : item.name)}
        </AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] leading-tight font-semibold text-text-primary">
          {isBlocked ? "Blocked user" : item.name}
        </p>
        {subtitleBadges.length > 0 ? (
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
            {subtitleBadges.map((badge) => (
              <span
                key={`${item.id}-${badge.label}`}
                className={cn(
                  "inline-flex h-[18px] shrink-0 items-center rounded-full px-1.5 text-[11px] leading-none font-semibold",
                  "bg-brand/10 text-brand dark:bg-brand/25 dark:text-violet-200",
                  badge.className,
                )}
              >
                {badge.label}
              </span>
            ))}
            {secondaryText ? (
              <p className="min-w-0 truncate text-sm text-text-secondary">
                {secondaryText}
              </p>
            ) : null}
          </div>
        ) : secondaryText ? (
          <p className="mt-0.5 truncate text-sm text-text-secondary">
            {secondaryText}
          </p>
        ) : null}
      </div>

      {trailingBadges.length > 0 || connectAs || item.trailingAction ? (
        <div className="flex shrink-0 items-center gap-2">
          {trailingBadges.map((badge) => (
            <Badge
              key={`${item.id}-${badge.label}`}
              variant={badge.variant}
              className={badge.className}
            >
              {badge.label}
            </Badge>
          ))}
          {connectAs ? (
            <ConnectButton
              onNavigate={onNavigate}
              targetUserId={item.id}
              targetName={item.name}
              relationship={relationship}
              surface={connectAs}
              appearance={connectAppearance}
            />
          ) : null}
          {item.trailingAction}
        </div>
      ) : null}
    </div>
  );

  const rowClassName = cn(
    "block border-b border-border/50 px-3 py-3 transition-colors last:border-b-0",
    isBlocked
      ? "cursor-default text-text-secondary"
      : "hover:bg-surface-alt/50 active:bg-surface-alt",
    className,
  );

  if (isBlocked || !item.href) {
    return <div className={rowClassName}>{rowContent}</div>;
  }

  return (
    <BackAwareLink
      href={item.href}
      className={rowClassName}
      onClick={onNavigate}
    >
      {rowContent}
    </BackAwareLink>
  );
}

function initials(value: string) {
  const cleaned = value.trim();
  if (!cleaned) {
    return "";
  }

  const parts = cleaned.split(/\s+/).slice(0, 2);
  if (parts.length === 1) {
    return parts[0]?.slice(0, 2).toUpperCase() ?? "";
  }

  return parts.map((part) => part.charAt(0).toUpperCase()).join("");
}
